import { useEffect, useId, useRef, useState } from "react";
import { Lock, StickyNote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  EditorContent,
  Extension,
  mergeAttributes,
  useEditor,
  type Editor,
  type JSONContent,
} from "@tiptap/react";
import Mention from "@tiptap/extension-mention";
import StarterKit from "@tiptap/starter-kit";

import { Alert } from "@lytra/frontend-common/components/v2/ui/alert";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import { cn } from "@/lib/utils";
import {
  afterFailure,
  clearDraft,
  draftStorageKey,
  keyForSubmit,
  loadDraft,
  MAX_NOTE_CHARS,
  noteComposerTriggerId,
  noteLength,
  saveDraft,
  type NoteComposerVariant,
  type NoteDraft,
} from "./noteDraft";
import { TICKET_NOTE_CREATE, TICKET_NOTE_UPDATE } from "./noteQueries";
import { mentionToken, noteDocToText, noteTextToDoc } from "./noteMentions";
import { createMentionSuggestion, PickerContext } from "./mentionSuggestion";
import { GET_AGENTS } from "./ticketQueries";

const DRAFT_DEBOUNCE_MS = 300;

const noop = (): void => undefined;

/**
 * Plain text only: paragraphs and line breaks. Every other StarterKit node and mark is
 * switched off, so neither typing, shortcuts nor a paste can produce bold, lists, links or
 * headings (UI-SPEC: editor content model). Undo history stays.
 */
const PLAIN_TEXT = StarterKit.configure({
  blockquote: false,
  bold: false,
  bulletList: false,
  code: false,
  codeBlock: false,
  dropcursor: false,
  heading: false,
  horizontalRule: false,
  italic: false,
  link: false,
  listItem: false,
  listKeymap: false,
  orderedList: false,
  strike: false,
  trailingNode: false,
  underline: false,
});

/** What is stored and sent: paragraphs and line breaks become a line break, a mention node its token. */
const readText = (editor: Editor): string => noteDocToText(editor.getJSON());

/** Moves the new card into view only if it is not fully visible (UI-SPEC: creating a note, step 4). */
function revealMessage(messageId: string): void {
  const card = document.querySelector(
    `[data-message-id="${CSS.escape(messageId)}"]`,
  );
  if (card && typeof card.scrollIntoView === "function") {
    // Instant, never animated: nothing here may move under prefers-reduced-motion.
    card.scrollIntoView({ block: "nearest" });
  }
}

interface NoteComposerProps {
  conversationId: string;
  variant: NoteComposerVariant;
  /**
   * The message answered (reply variant). A reply is stored pointing at it (D-13, D-17b).
   * With the edit variant it is only set when the edited message is a reply, and then only
   * picks the editor's name ("Antwort" instead of "Notiz").
   */
  parentMessageId?: string;
  /** Edit variant: the stored note or reply being changed. */
  messageId?: string;
  /** Called with the new message id once the note is stored and the ticket reloaded. */
  onSaved?: (messageId: string) => void;
  /** Called when the writer gives up (Abbrechen, or Escape in an empty editor). */
  onCancel?: () => void;
  /** Reply variant: focus the editor when it mounts. */
  autoFocus?: boolean;
  /** Whether the editor holds any text, reported on every change (and once on mount). */
  onHasTextChange?: (hasText: boolean) => void;
  /** Hands the Tiptap editor to the host; the tests drive it. */
  onEditorReady?: (editor: Editor) => void;
  /** The signed-in user: left out of the @ picker, and their chip reads in the accent colour. */
  viewerId?: string;
  /** A stored note text (tokens included) the editor starts with, unless a draft exists (edit variant). */
  initialText?: string;
  /** Extra line under the hint, e.g. that a reply under a send stays in Lytra (#717). */
  caption?: string;
}

/**
 * The composer of an internal note (D-14) and of a reply in a note's thread (D-13). The
 * note variant sits collapsed as one "Notiz hinzufügen" button until it is clicked
 * (D-17b: bottom of the ticket thread); the reply variant is expanded as soon as it
 * exists, its parent decides when that is.
 */
export function NoteComposer({
  conversationId,
  variant,
  parentMessageId,
  messageId,
  onSaved,
  onCancel,
  autoFocus = false,
  onHasTextChange,
  onEditorReady,
  viewerId,
  initialText,
  caption,
}: NoteComposerProps) {
  const { t } = useTranslation();
  const storageKey = draftStorageKey(
    conversationId,
    variant,
    variant === "edit" ? messageId : parentMessageId,
  );
  // A half-written note of this ticket reopens expanded with its text. Read again each time
  // the form opens: the "note" composer stays mounted while collapsed, and a saved or
  // cancelled draft is cleared from storage, so the next note gets a fresh idempotency key
  // instead of the previous note's (which the server would answer with the old note).
  // The edit variant mints no idempotency key: an update is idempotent by itself.
  const readDraft = () =>
    loadDraft(
      window.sessionStorage,
      storageKey,
      variant === "edit" ? () => "" : undefined,
    );
  const [initialDraft, setInitialDraft] = useState(readDraft);
  const [expanded, setExpanded] = useState(
    variant !== "note" || initialDraft.text !== "" || !!initialText,
  );
  // Only a click on the trigger (or `autoFocus`) moves focus; a restored draft must not steal it.
  const [focusOnMount, setFocusOnMount] = useState(autoFocus);
  const [status, setStatus] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef(false);

  useEffect(() => {
    if (!expanded && returnFocusRef.current) {
      returnFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [expanded]);

  if (variant === "note" && !expanded) {
    return (
      <>
        <Button
          ref={triggerRef}
          id={noteComposerTriggerId(conversationId)}
          type="button"
          variant="outline"
          size="md"
          leadingIcon={StickyNote}
          onClick={() => {
            setInitialDraft(readDraft());
            setFocusOnMount(true);
            setExpanded(true);
          }}
        >
          {t("tickets.notes.addNote")}
        </Button>
        <span role="status" className="sr-only">
          {status}
        </span>
      </>
    );
  }

  return (
    <>
      <NoteComposerForm
        conversationId={conversationId}
        variant={variant}
        parentMessageId={parentMessageId}
        messageId={messageId}
        storageKey={storageKey}
        initialDraft={initialDraft}
        focusOnMount={focusOnMount}
        onHasTextChange={onHasTextChange}
        onEditorReady={onEditorReady}
        viewerId={viewerId}
        initialText={initialText}
        caption={caption}
        onSaved={(savedId) => {
          returnFocusRef.current = true;
          setStatus(
            t(
              variant === "note"
                ? "tickets.notes.status.saved"
                : "tickets.notes.status.replySaved",
            ),
          );
          setExpanded(false);
          if (variant !== "edit")
            requestAnimationFrame(() => revealMessage(savedId));
          onSaved?.(savedId);
        }}
        onCancel={() => {
          returnFocusRef.current = true;
          setExpanded(false);
          onCancel?.();
        }}
      />
      {variant === "note" && (
        <span role="status" className="sr-only">
          {status}
        </span>
      )}
    </>
  );
}

interface NoteComposerFormProps {
  conversationId: string;
  variant: NoteComposerVariant;
  parentMessageId?: string;
  messageId?: string;
  storageKey: string;
  initialDraft: NoteDraft;
  focusOnMount: boolean;
  onSaved: (messageId: string) => void;
  onCancel: () => void;
  onHasTextChange?: (hasText: boolean) => void;
  onEditorReady?: (editor: Editor) => void;
  viewerId?: string;
  initialText?: string;
  caption?: string;
}

type FieldError = "empty" | "tooLong" | null;

function NoteComposerForm({
  conversationId,
  variant,
  parentMessageId,
  messageId,
  storageKey,
  initialDraft,
  focusOnMount,
  onSaved,
  onCancel,
  onHasTextChange,
  onEditorReady,
  viewerId,
  initialText,
  caption,
}: NoteComposerFormProps) {
  const { t } = useTranslation();
  const captionId = useId();
  const errorId = useId();
  // An edited reply is a reply: it carries its parent, only to be named right.
  const isReplyLike = variant === "reply" || parentMessageId !== undefined;
  const [fieldError, setFieldError] = useState<FieldError>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // A draft wins over the text the host seeds: it is what the writer last left behind.
  const startText =
    initialDraft.text !== "" ? initialDraft.text : (initialText ?? "");
  const [isEmpty, setIsEmpty] = useState(startText.trim() === "");

  // The tenant's colleagues for the @ picker: cache-first, already fetched by the ticket.
  const {
    data: agentsData,
    loading: agentsLoading,
    error: agentsError,
  } = useQuery(GET_AGENTS, { errorPolicy: "all" });
  const [picker] = useState(() => new PickerContext(viewerId));
  const agents = agentsData?.agents;
  const agentsStatus: PickerContext["status"] = agents
    ? "ready"
    : agentsError && !agentsLoading
      ? "error"
      : "loading";
  useEffect(() => {
    // An open picker shows the answer that arrived after it opened.
    picker.update(agents ?? [], agentsStatus, viewerId);
  }, [picker, agents, agentsStatus, viewerId]);

  // `errorPolicy: "none"` so a GraphQL refusal rejects, whatever the client default is.
  const [createNote] = useMutation(TICKET_NOTE_CREATE, {
    errorPolicy: "none",
    refetchQueries: ["Ticket"],
    awaitRefetchQueries: true,
  });
  const [updateNote] = useMutation(TICKET_NOTE_UPDATE, {
    errorPolicy: "none",
    refetchQueries: ["Ticket"],
    awaitRefetchQueries: true,
  });

  const draftRef = useRef<NoteDraft>(initialDraft);
  const latestTextRef = useRef(startText);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set once the draft is gone for good (saved or cancelled): nothing may write it back.
  const finishedRef = useRef(false);
  const savingRef = useRef(false);

  // The editor's keyboard shortcuts run outside React's render, so they call the latest handlers.
  const actionsRef = useRef({ submit: noop, cancel: noop });
  const editorRef = useRef<Editor | null>(null);

  const persist = () => {
    if (finishedRef.current) return;
    if (latestTextRef.current === "") {
      clearDraft(window.sessionStorage, storageKey);
      return;
    }
    // An edit identical to the stored text is no draft: the next edit starts from the stored note.
    if (variant === "edit" && latestTextRef.current === (initialText ?? "")) {
      clearDraft(window.sessionStorage, storageKey);
      return;
    }
    draftRef.current = { ...draftRef.current, text: latestTextRef.current };
    saveDraft(window.sessionStorage, storageKey, draftRef.current);
  };

  const editor = useEditor({
    extensions: [
      PLAIN_TEXT,
      Mention.configure({
        deleteTriggerWithBackspace: true,
        // Typed "@Anna" stays plain text; only a node picked from the list is a mention (T-02-29).
        renderText: ({ node }) =>
          mentionToken(
            String(node.attrs.id ?? ""),
            String(node.attrs.label ?? ""),
          ),
        // Element nodes with text content only: a name is never parsed as markup (T-02-28).
        renderHTML: ({ options, node }) => [
          "span",
          mergeAttributes(options.HTMLAttributes, {
            "data-slot": "mention-chip",
            class: cn(
              "rounded-sm bg-primary-subtle px-1 text-label whitespace-nowrap",
              node.attrs.id === picker.viewerId
                ? "text-primary-strong"
                : "text-ink",
            ),
          }),
          `@${String(node.attrs.label ?? "")}`,
        ],
        suggestion: createMentionSuggestion(picker),
      }),
      Extension.create({
        name: "noteSubmit",
        addKeyboardShortcuts() {
          return {
            "Mod-Enter": () => {
              actionsRef.current.submit();
              return true;
            },
            Escape: () => {
              if (noteDocToText(this.editor.getJSON()).trim() !== "")
                return false;
              actionsRef.current.cancel();
              return true;
            },
          };
        },
      }),
    ],
    content: noteTextToDoc(startText),
    autofocus: focusOnMount ? "end" : false,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": t(
          isReplyLike
            ? "tickets.notes.editorLabelReply"
            : "tickets.notes.editorLabelNote",
        ),
        "aria-describedby":
          variant === "edit"
            ? errorId
            : `${caption ? `${captionId} ` : ""}${errorId}`,
        class:
          "min-h-14 whitespace-pre-wrap break-words text-body text-ink outline-none",
      },
      // Plain text only, whatever the clipboard holds: formatting and HTML are dropped.
      handlePaste: (_view, event) => {
        const text = event.clipboardData?.getData("text/plain") ?? "";
        const inline: JSONContent[] = [];
        text.split(/\r\n|\r|\n/).forEach((line, index) => {
          if (index > 0) inline.push({ type: "hardBreak" });
          if (line !== "") inline.push({ type: "text", text: line });
        });
        if (inline.length > 0)
          editorRef.current?.commands.insertContent(inline);
        return true;
      },
    },
    onCreate: ({ editor: created }) => {
      editorRef.current = created;
      onEditorReady?.(created);
      onHasTextChange?.(readText(created).trim() !== "");
    },
    onUpdate: ({ editor: updated }) => {
      const text = readText(updated);
      latestTextRef.current = text;
      const hasText = text.trim() !== "";
      setIsEmpty(!hasText);
      setFieldError(null);
      onHasTextChange?.(hasText);
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(persist, DRAFT_DEBOUNCE_MS);
    },
  });

  const finish = () => {
    finishedRef.current = true;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    clearDraft(window.sessionStorage, storageKey);
  };

  const submit = async () => {
    // A second click, or Mod-Enter, while the first is in flight sends nothing (T-02-25).
    if (savingRef.current || !editor || editor.isDestroyed) return;
    const text = readText(editor).trim();
    if (text === "") {
      setFieldError("empty");
      editor.commands.focus();
      return;
    }
    if (noteLength(text) > MAX_NOTE_CHARS) {
      setFieldError("tooLong");
      return;
    }
    setFieldError(null);
    setSaveFailed(false);

    // The key comes from the draft, never from the click: a retry of this body is the same note.
    const { clientRequestId, draft } = keyForSubmit(draftRef.current, text);
    draftRef.current = draft;
    saveDraft(window.sessionStorage, storageKey, draft);

    savingRef.current = true;
    setSaving(true);
    editor.setEditable(false);
    try {
      if (variant === "edit") {
        if (!messageId) throw new Error("edit needs a messageId");
        const result = await updateNote({ variables: { messageId, text } });
        finish();
        if (!result.data?.noteUpdate) {
          // Deleted elsewhere, or not the viewer's: nothing to save any more. The mutation's
          // refetchQueries already reloaded the ticket; the editor closes.
          toast(t("tickets.notes.errors.gone"));
          onCancel();
          return;
        }
        onSaved(messageId);
        return;
      }
      const result = await createNote({
        variables: {
          conversationId,
          text,
          clientRequestId,
          parentMessageId: parentMessageId ?? null,
        },
      });
      const createdId = result.data?.noteCreate.id;
      if (!createdId) throw new Error("noteCreate returned no message");
      finish();
      if (!editor.isDestroyed) editor.commands.clearContent(true);
      onSaved(createdId);
    } catch {
      draftRef.current = afterFailure(draftRef.current, text);
      saveDraft(window.sessionStorage, storageKey, draftRef.current);
      if (!editor.isDestroyed) editor.setEditable(true);
      setSaveFailed(true);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const cancel = () => {
    if (savingRef.current) return;
    finish();
    if (editor && !editor.isDestroyed) editor.commands.clearContent(true);
    onCancel();
  };

  useEffect(() => {
    actionsRef.current = { submit: () => void submit(), cancel };
  });

  // A ticket switch or a collapsed thread unmounts the form: keep what was typed.
  useEffect(
    () => () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persist();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persist reads refs and the stable storage key only
    [],
  );

  const prompt = t(
    isReplyLike ? "tickets.notes.promptReply" : "tickets.notes.promptNote",
  );

  return (
    <div
      data-slot="note-composer"
      className="flex w-full max-w-[65ch] min-w-0 flex-col gap-3 rounded-xl border border-dashed border-border bg-surface-subtle p-4"
    >
      {variant === "note" && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-label text-ink">
            <Icon icon={Lock} className="text-ink-muted" />
            {t("tickets.notes.composerTitle")}
          </div>
          <p className="text-caption text-ink-muted">
            {t("tickets.notes.visibility")}
          </p>
        </div>
      )}
      <div
        data-slot="note-editor"
        className={cn(
          "relative max-h-60 min-h-20 cursor-text overflow-y-auto rounded-md border border-input bg-background px-3 py-2",
          "outline-ring hover:border-ink-muted focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-solid",
          fieldError && "border-destructive outline-destructive",
        )}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && editor) {
            event.preventDefault();
            editor.commands.focus("end");
          }
        }}
      >
        {isEmpty && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-2 left-3 text-body text-ink-muted"
          >
            {prompt}
          </span>
        )}
        <EditorContent editor={editor} />
      </div>
      {variant !== "edit" && caption && (
        <p id={captionId} className="text-caption text-ink-muted">
          {caption}
        </p>
      )}
      {fieldError && (
        <p id={errorId} role="alert" className="text-caption text-destructive">
          {t(
            fieldError === "empty"
              ? "tickets.notes.errors.empty"
              : "tickets.notes.errors.tooLong",
          )}
        </p>
      )}
      {saveFailed && (
        <Alert tone="error">{t("tickets.notes.errors.saveFailed")}</Alert>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="md"
          disabled={saving}
          onClick={cancel}
        >
          {t("tickets.notes.cancel")}
        </Button>
        <Button
          type="button"
          variant="default"
          size="md"
          loading={saving}
          onClick={() => void submit()}
        >
          {t(
            variant === "note"
              ? "tickets.notes.saveNote"
              : variant === "edit"
                ? "tickets.notes.saveEdit"
                : "tickets.notes.saveReply",
          )}
        </Button>
      </div>
    </div>
  );
}
